/* eslint-disable react-func/max-lines-per-function */
import { useEffect, useState, useCallback, useRef } from 'react';
import { clone, uniqueId } from 'lodash-es';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocalStorageState } from 'ahooks';
import { FileACL, TaskDetailPageParams } from 'common/types';
import useAntStaticComponent from 'common/hooks/useAntStaticComponent';
import {
  selectCurComment,
  selectFilteredCommentList,
  setFilteredCommentList,
  setGroupedComments,
  setIsCommentDrawerOpen,
  setCurComment as setCurCommentAction,
  selectCurCommentId,
  setCurCommentId as setCurCommentIdAction,
  setCurrentCommentIndex,
  selectCurrentCommentIndex,
  selectIsInspecting,
  setIsInspecting as setIsInspectingAction,
} from '@/store/comment';

import { useAppSelector, useAppDispatch } from '@/hooks';
import service from '@/service';
import { AttachmentFile, Comment, CommentOrderBy, CommentFilterBy } from '@/types';
import { blobToFile, dataURItoBlob, eventEmitter } from '@/utils';
import { PlayerViewerEvent } from '@/components/PlayViewerControl';
import { selectUserInfo } from '@/store/user';
import { UploadFileType } from '@/constants';
import { useTaskEditorContext } from '../TaskEditorContext';
import { db, CommentColumn } from './db';
import { UploadManager } from 'common/hooks/upload/UploadManager';
import { RcFile } from 'antd/es/upload';
import { getFormattedAttachment } from '@/utils/comment';

export const useComment = () => {
  const { taskId, versionId } = useParams() as TaskDetailPageParams; //任务id
  const [orderBy, setOrderBy] = useState<CommentOrderBy>(CommentOrderBy.TIME); //排序方式
  const [filterBy, setFilterBy] = useState<CommentFilterBy>(CommentFilterBy.ALL); //筛选方式
  const [currentFile, setCurrentFile] = useState<string>('');
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { message: antMessage } = useAntStaticComponent();
  const userInfo = useAppSelector(selectUserInfo);
  const curComment = useAppSelector(selectCurComment);
  const curCommentId = useAppSelector(selectCurCommentId);
  const curCommentIndex = useAppSelector(selectCurrentCommentIndex);
  const isInspecting = useAppSelector(selectIsInspecting);
  const { playList, isPlayList } = useTaskEditorContext();
  const [content, setContent] = useLocalStorageState<string>(`eevee-editor-content-${taskId}-${versionId}`, {
    defaultValue: '',
  });
  const [isChecked, setIsChecked] = useState(true); //是否选中了携带预览区域内容
  const [attachment, setAttachment] = useState<Array<AttachmentFile>>([]);
  const [searchVersion, setSearchVersion] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false); //标记当前的修改状态
  const [isPreviewing, setIsPreviewing] = useState(false); // 标记当前是否正在还原预览绘制内容

  // const [isInspecting, setIsInspecting] = useState(true); //反馈事件被禁用，用于在批注模式下，禁用反馈事件
  const filteredCommentList = useAppSelector(selectFilteredCommentList); // 包含了两个部分，server上存储的comments，和本地未发布成功的comments

  const um = useRef(new UploadManager({ autoUpload: true }));

  const getReviewList = useCallback(() => {
    console.log('getReviewList', taskId, searchVersion, currentFile);
    return getFilteredCommentListByVersion(orderBy, filterBy);
  }, [orderBy, filterBy, currentFile]);
  const { isLoading: isCommentsLoading } = useQuery(
    ['getReviewList', taskId, searchVersion, currentFile],
    getReviewList,
    {
      enabled: !!taskId && !!searchVersion,
    },
  );

  const setCurComment = (comment: Comment) => {
    dispatch(setCurCommentAction(comment));
  };

  const setIsInspecting = (isInspecting: boolean) => {
    dispatch(setIsInspectingAction(isInspecting));
  };
  useEffect(() => {
    const comment = filteredCommentList.find((com) => com.uniqueId === curCommentId);
    comment && setCurComment(comment);
  }, [filteredCommentList, curCommentId]);

  // const resetCachedCommentList = cachedFileList => {
  //   const newCachedCommentList: Comment[] = [];
  //   cachedFileList?.forEach(item => {
  //     newCachedCommentList.push({ ...item, isSyncingToServer: false, extra: { ...item.extra, isUpdateError: true } });
  //   });

  //   return newCachedCommentList;
  // };
  const resetCachedCommentList = (list: CommentColumn[]) => {
    const newCachedCommentList: Comment[] = [];
    list?.forEach((item) => {
      const com = JSON.parse(item.comment);
      newCachedCommentList.push({ ...com, isSyncingToServer: false, extra: { ...com.extra, isUpdateError: true } });
    });

    return newCachedCommentList;
  };

  const getFilteredCommentListByVersion = async (orderByUser?: CommentOrderBy, filterByUser?: CommentFilterBy) => {
    const reviewList = await service.comments.getReviews(
      taskId,
      versionId,
      orderByUser || orderBy,
      // (filterByUser || filterBy) === CommentFilterBy.ALL ? undefined : currentFile,
    );

    let filtered: Comment[] = reviewList.map((comment) => {
      const assetPosition = comment.assetPosition ? JSON.parse(comment.assetPosition) : {};
      // console.log('assetPosition', assetPosition, playList);
      // if ((filterByUser || filterBy) !== CommentFilterBy.ALL) {
      //   if (isPlayList) {
      //     if (playList.findIndex(item => item.uuid === assetPosition.fileId) === -1) {
      //       return null;
      //     }
      //   } else if (assetPosition.fileName !== currentFile) {
      //     return null;
      //   }
      // }
      return {
        ...comment,
        annotation: comment.annotation ? JSON.parse(comment.annotation) : {},
        assetPosition: assetPosition,
      };
    });
    const result = await db.comments.where({ taskId: Number(taskId), versionId: Number(versionId) });
    const arr = await result.toArray();
    if (arr && arr.length > 0) {
      const resetCachedList = resetCachedCommentList(arr);
      filtered = [...resetCachedList, ...filtered];
    }
    dispatch(setFilteredCommentList(filtered));
    groupCommentByFileId(filtered);
    return filtered;
  };

  const getCommentList = async () => {
    const reviewList = await service.comments.getReviews(taskId, versionId);
    let filtered: Comment[] = reviewList.map((comment) => {
      return {
        ...comment,
        annotation: comment.annotation ? JSON.parse(comment.annotation) : {},
        assetPosition: comment.assetPosition ? JSON.parse(comment.assetPosition) : {},
      };
    });
    const result = await db.comments.where({ taskId: Number(taskId), versionId: Number(versionId) });
    const arr = await result.toArray();
    if (arr && arr.length > 0) {
      const resetCachedList = resetCachedCommentList(arr);
      filtered = [...resetCachedList, ...filtered];
    }
    return filtered;
  };

  const groupCommentByFileId = (arr: Comment[]) => {
    const map = new Map();
    arr.forEach((item) => {
      const key = item.assetPosition?.fileId;
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });

    const groupedComments = Object.fromEntries(map);

    dispatch(setGroupedComments(groupedComments));
  };

  const saveToServer = ({ imageBlob, comment, id, type, versionId }) => {
    return new Promise(async (resolve, reject) => {
      //上传文件
      try {
        const imageFile = imageBlob ? blobToFile(imageBlob, `${uuidv4()}.png`, imageBlob.type) : null;
        let preview;
        if (imageFile) {
          // preview = await service.upload.uploadFileByProxy(imageFile, UploadFileType.Public, false);
          preview = await generateCommentPreview(imageFile);
        }
        const formattedAttachment = getFormattedAttachment(comment.attachment);
        const obj = {
          ...comment,
          content: comment.content ?? '',
          attachment: formattedAttachment ?? [],
          preview: comment.preview,
        };
        obj.preview = preview || {};
        const { code: addReviewCode, message: addReviewMessage } = await service.comments.addReview(
          versionId,
          id,
          obj,
          type,
        );

        if (addReviewCode === 0) {
          resolve(obj);
        } else {
          antMessage.error(addReviewMessage);
          reject(new Error(addReviewMessage));
        }
      } catch (err) {
        reject(err);
      }
    });
  };
  const imageBlobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onload = function () {
        resolve(reader.result as string);
      };
      reader.onerror = function (error) {
        reject(error);
      };
    });
  };

  const onSaveSuccess = (newCommentList, newComment) => {
    db.comments
      .delete(newComment.uniqueId)
      .then(() => {
        console.log('缓存删除成功');
      })
      .catch((err) => {
        console.error('缓存记录删除失败：', err);
      });

    antMessage.success('反馈发布成功');
    onUpdateSuccess(newCommentList, newComment);
    getFilteredCommentListByVersion();
  };
  const saveComment = async ({ jsonObj, versionId, imageBlob, versionName, id, type, onSuccess, onError }) => {
    try {
      // 给commentList加一条数据
      console.log('jsonObj', jsonObj);

      let newCommentList = [...filteredCommentList];
      const imageUrl = imageBlob ? await imageBlobToBase64(imageBlob) : '';
      const time = dayjs().format('YYYY-MM-DD HH:mm:ss');
      const newComment = {
        uniqueId: Number(uniqueId()),
        type,
        content: jsonObj.content ?? '',
        preview: jsonObj.preview ?? '',
        assetPosition: jsonObj.assetPosition,
        attachment: jsonObj.attachment,
        annotation: jsonObj.annotation,
        version: versionName,
        creator: {
          nickName: userInfo?.nickName ?? '',
          email: '',
          avatar: userInfo?.avatar ?? '',
          uniqueId: userInfo?.uniqueId ?? -1,
        },
        updateTime: time,
        createTime: time,
        isFullScreen: false,
        popo: '',
        isSyncingToServer: true,
        extra: {
          imageUrl,
          id,
        },
      };
      //创建一个反馈
      newCommentList.unshift(newComment);
      dispatch(setFilteredCommentList(newCommentList));

      try {
        // 存在indexedDB中
        await db.comments.add({
          id: newComment.uniqueId,
          comment: JSON.stringify(newComment),
          taskId: Number(taskId),
          versionId,
        });

        console.log('记录添加成功');
      } catch (err) {
        console.error('记录添加失败：', err);
      }

      try {
        //如果成功，返回一个新的comment
        const newObj = await saveToServer({
          imageBlob: imageBlob,
          comment: newComment,
          versionId: versionId,
          id,
          type,
        });

        onSaveSuccess(newCommentList, newComment);

        onSuccess(newObj);
      } catch (err) {
        onUpdateError(newCommentList, newComment);
      }
    } catch (err) {
      console.log(err);

      onError();
    }
  };

  const onCommentDelete = async (reviewId: number) => {
    //如果当前正在还原注释反馈，需要清空
    dispatch(setIsCommentDrawerOpen(false));
    eventEmitter.emit(PlayerViewerEvent.ClearAnnotation);
    await service.comments.deleteReview(reviewId);
    // await queryClient.refetchQueries(['getReviewList']);
    await getFilteredCommentListByVersion();
  };

  const onUpdateSuccess = (newCommentList, newComment) => {
    let list = clone(newCommentList);
    //找到当前的comment
    list = list.map((item) => {
      if (item.uniqueId === newComment.uniqueId) {
        item = { ...item, isSyncingToServer: false, extra: { ...newComment.extra, isUpdateError: false } };
      }
      return item;
    });
    dispatch(setFilteredCommentList(list));
  };

  const onUpdateError = (newCommentList, comment: Comment) => {
    console.log('onUpdateError');
    let list = clone(newCommentList);
    //把没有发布成功的comment，标记为发布失败状态，isUpdateError:true
    list = list.map((item) => {
      if (item.uniqueId === comment.uniqueId) {
        item = { ...item, isSyncingToServer: false, extra: { ...comment.extra, isUpdateError: true } };
      }
      return item;
    });
    dispatch(setFilteredCommentList(list));
  };

  const retrySaveComment = async (comment: Comment) => {
    try {
      let list = clone(filteredCommentList);
      let newComment = { ...comment, isSyncingToServer: true, extra: { ...comment.extra, isUpdateError: false } };
      list = list.map((item) => {
        if (item.uniqueId === comment.uniqueId) {
          item = newComment;
        }
        return item;
      });
      dispatch(setFilteredCommentList(list));
      const imageBlob = comment.extra.imageUrl ? dataURItoBlob(comment.extra.imageUrl) : null;
      //重新发布反馈
      try {
        const obj = await saveToServer({
          imageBlob,
          comment,
          versionId: versionId,
          id: comment.extra.id,
          type: comment.type,
        });

        newComment = { ...newComment, isSyncingToServer: false, extra: { ...comment.extra, isUpdateError: false } };
        antMessage.success('反馈发布成功');
        list = list.map((item) => {
          if (item.uniqueId === comment.uniqueId) {
            item = newComment;
          }
          return item;
        });
        dispatch(setFilteredCommentList(list));

        //缓存的数据也清空
        db.comments.delete(comment.uniqueId);

        eventEmitter.emit(PlayerViewerEvent.ClearAnnotation);

        getFilteredCommentListByVersion();
      } catch (e) {
        onUpdateError(list, newComment);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const cancelSave = useCallback(
    (comment: Comment) => {
      const index = filteredCommentList.findIndex((item) => item.uniqueId === comment.uniqueId);
      let list = clone(filteredCommentList);
      list.splice(index, 1);
      dispatch(setFilteredCommentList(list));
      //缓存的数据也清空
      db.comments.delete(comment.uniqueId);
      eventEmitter.emit(PlayerViewerEvent.ClearAnnotation);
    },
    [filteredCommentList],
  );
  const generateCommentPreview = (imageBlob) => {
    return new Promise(async (resolve, reject) => {
      const file = blobToFile(imageBlob, `${uuidv4()}.png`, imageBlob.type);
      // 需要加一个uid
      Object.assign(file, { uid: uuidv4() });
      // const downloadUrl = await service.upload.uploadFileByProxy(file, UploadFileType.ReviewPreview, false);
      um.current.add(file as RcFile, {
        params: {
          acl: FileACL.Public,
        },
        onFinished: (res, file) => {
          resolve({ ...res, fileSize: file.size.toString() });
        },
        onFailed(error) {
          console.error(error);
        },
      });
    });
  };

  const updateComment = async ({ taskId, comment, imageBlob, reviewId }) => {
    let newComment = { ...comment };
    let uploadRes;
    if (imageBlob) {
      uploadRes = await generateCommentPreview(imageBlob);
    }
    newComment = { ...newComment, preview: uploadRes };

    const { code } = await service.comments.updateReview({ taskId, reviewId, comment: newComment });
    if (code === 0) {
      antMessage.success('反馈更新成功');
      // queryClient.refetchQueries(['getReviewList']);
      getFilteredCommentListByVersion();
    } else {
      antMessage.error('反馈更新失败');
    }
  };

  const setCurCommentId = (id: number) => {
    dispatch(setCurCommentIdAction(id));
  };

  const setCurCommentIndex = (index) => {
    dispatch(setCurrentCommentIndex(index));
  };

  return {
    getFilteredCommentListByVersion,
    groupCommentByFileId,
    saveComment,
    filteredCommentList,
    isCommentsLoading,
    onCommentDelete,
    searchVersion,
    setSearchVersion,
    isEditing,
    setIsEditing,
    // onUpdateError,
    retrySaveComment,
    cancelSave,
    curCommentId,
    setCurCommentId,
    updateComment,
    curComment,
    setCurComment,
    isPreviewing,
    setIsPreviewing,
    isInspecting,
    setIsInspecting,
    setCurCommentIndex,
    content,
    setContent,
    attachment,
    setAttachment,
    isChecked,
    setIsChecked,
    setOrderBy,
    filterBy,
    currentFile,
    setFilterBy,
    setCurrentFile,
    getCommentList,
  };
};
